import { useCallback, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import type { Session } from '../../../api/sessionsService';
import type { ForecastAvailability, ActivityAvailability } from '../types';
import { clampTimelineSelection, dateToX, xToDate } from '../utils/coordinateUtils';

interface TimeBounds {
  start: Date;
  end: Date;
}

type HoveredDataBar =
  | { type: string; start: Date; end: Date }
  | { type: string; issuanceTime: Date };

export interface UseTimelineInteractionParams {
  canvasRef: RefObject<HTMLCanvasElement | null>;
  sessions: Session[];
  selectedRange: { start: Date; end: Date } | null;
  selectedPoint: Date | null;
  setSelectedRange: React.Dispatch<React.SetStateAction<{ start: Date; end: Date } | null>>;
  setSelectedPoint: React.Dispatch<React.SetStateAction<Date | null>>;
  setIsLiveMode: React.Dispatch<React.SetStateAction<boolean>>;
  mode: 'range' | 'point';
  forecastAvailability?: ForecastAvailability;
  radarActivityAvailability?: ActivityAvailability;
  stationActivityAvailability?: ActivityAvailability;
  /** Visible time window for coordinate conversion */
  getViewBounds: () => TimeBounds | null;
  /** Full event bounds for pan clamping */
  getEventBounds: () => TimeBounds | null;
  setViewBounds: React.Dispatch<React.SetStateAction<TimeBounds | null>>;
  onDateRangeChange?: (startDate: Date, endDate: Date) => void;
  onDatePointChange?: (date: Date) => void;
  /** Cap selection at the current instant (Observations during active events). */
  clampSelectionToNow?: boolean;
  /** Compact layout with reduced bar sizes — must match useTimelineCanvas. */
  compact?: boolean;
}

export interface UseTimelineInteractionReturn {
  // Mouse handlers
  handleMouseDown: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  handleMouseMove: (e: React.MouseEvent<HTMLCanvasElement>) => void;
  handleMouseUp: () => void;
  handleMouseLeave: () => void;
  // Time adjustments
  adjustStartTime: (minutes: number) => void;
  adjustEndTime: (minutes: number) => void;
  // Drag state (for cursor)
  isDragging: boolean;
  isDraggingRange: boolean;
  isPanning: boolean;
  isDraggingEdge: 'start' | 'end' | null;
  isHoveringRange: boolean;
  isHoveringEdge: 'start' | 'end' | null;
  // Tooltip state
  hoveredSession: Session | null;
  hoveredDataBar: HoveredDataBar | null;
  tooltipPosition: { x: number; y: number } | null;
  // Hover time (time under cursor)
  hoverTime: Date | null;
}

export function useTimelineInteraction(
  params: UseTimelineInteractionParams
): UseTimelineInteractionReturn {
  const {
    canvasRef,
    sessions,
    selectedRange,
    selectedPoint,
    setSelectedRange,
    setSelectedPoint,
    setIsLiveMode,
    mode,
    forecastAvailability,
    radarActivityAvailability,
    stationActivityAvailability,
    getViewBounds,
    getEventBounds,
    setViewBounds,
    onDateRangeChange,
    onDatePointChange,
    clampSelectionToNow = false,
    compact = false,
  } = params;

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState<{ x: number; viewStart: number; viewEnd: number } | null>(null);
  const [isDraggingRange, setIsDraggingRange] = useState(false);
  const [dragRangeOffset, setDragRangeOffset] = useState<number>(0);
  const [isHoveringRange, setIsHoveringRange] = useState(false);
  const [isDraggingEdge, setIsDraggingEdge] = useState<'start' | 'end' | null>(null);
  const [isHoveringEdge, setIsHoveringEdge] = useState<'start' | 'end' | null>(null);
  const [hoveredSession, setHoveredSession] = useState<Session | null>(null);
  const [hoveredDataBar, setHoveredDataBar] = useState<HoveredDataBar | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const [hoverTime, setHoverTime] = useState<Date | null>(null);

  const adjustmentTimeoutRef = useRef<number | null>(null);

  const getEdgeAtPosition = useCallback(
    (x: number, bounds: TimeBounds, width: number): 'start' | 'end' | null => {
      if (!selectedRange) return null;
      const startX = dateToX(selectedRange.start, bounds, width);
      const endX = dateToX(selectedRange.end, bounds, width);
      const edgeThreshold = 8;
      if (Math.abs(x - startX) <= edgeThreshold) return 'start';
      if (Math.abs(x - endX) <= edgeThreshold) return 'end';
      return null;
    },
    [selectedRange]
  );

  const isPositionInSelectedRange = useCallback(
    (x: number, bounds: TimeBounds, width: number): boolean => {
      if (!selectedRange) return false;
      const startX = dateToX(selectedRange.start, bounds, width);
      const endX = dateToX(selectedRange.end, bounds, width);
      return x >= startX && x <= endX;
    },
    [selectedRange]
  );

  const getSessionAtPosition = useCallback(
    (x: number, _y: number, bounds: TimeBounds, width: number): Session | null => {
      for (const session of sessions) {
        const startX = dateToX(new Date(session.start_time), bounds, width);
        const endX = dateToX(new Date(session.end_time), bounds, width);
        if (x >= startX && x <= endX) return session;
      }
      return null;
    },
    [sessions]
  );

  const getDataBarAtPosition = useCallback(
    (
      x: number,
      y: number,
      bounds: TimeBounds,
      width: number,
      height: number
    ): HoveredDataBar | null => {
      const barHeight = compact ? 4 : 8;
      const barSpacing = compact ? 1 : 2;
      let barOffset = height - (compact ? 6 : 10);

      const isInBar = (yPos: number, barTop: number, h: number): boolean =>
        yPos >= barTop && yPos <= barTop + h;
      const isNearLine = (xPos: number, lineX: number): boolean =>
        Math.abs(xPos - lineX) <= 3;

      if (stationActivityAvailability?.ranges?.length) {
        if (isInBar(y, barOffset, barHeight)) {
          for (const range of stationActivityAvailability.ranges) {
            const startX = dateToX(range.start, bounds, width);
            const endX = dateToX(range.end, bounds, width);
            if (x >= startX && x <= endX) {
              return { type: 'Station Activity', start: range.start, end: range.end };
            }
          }
        }
        barOffset -= barHeight + barSpacing;
      }

      if (radarActivityAvailability?.ranges?.length) {
        if (isInBar(y, barOffset, barHeight)) {
          for (const range of radarActivityAvailability.ranges) {
            const startX = dateToX(range.start, bounds, width);
            const endX = dateToX(range.end, bounds, width);
            if (x >= startX && x <= endX) {
              return { type: 'Radar Activity', start: range.start, end: range.end };
            }
          }
        }
        barOffset -= barHeight + barSpacing;
      }

      if (forecastAvailability?.daily?.length) {
        if (isInBar(y, barOffset, barHeight)) {
          for (const issuanceTime of forecastAvailability.daily) {
            const lineX = dateToX(issuanceTime, bounds, width);
            if (isNearLine(x, lineX)) {
              return { type: 'Daily Forecast Issued', issuanceTime };
            }
          }
        }
        barOffset -= barHeight + barSpacing;
      }

      if (forecastAvailability?.hourlyIssuance?.length) {
        if (isInBar(y, barOffset, barHeight)) {
          for (const issuanceTime of forecastAvailability.hourlyIssuance) {
            const lineX = dateToX(issuanceTime, bounds, width);
            if (isNearLine(x, lineX)) {
              return { type: 'Hourly Forecast Issued', issuanceTime };
            }
          }
        }
        barOffset -= barHeight + barSpacing;
      }

      return null;
    },
    [forecastAvailability, radarActivityAvailability, stationActivityAvailability, compact]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const viewBounds = getViewBounds();
      const eventBounds = getEventBounds();
      if (!canvas || !viewBounds || !eventBounds) return;

      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;

      // Shift+drag: pan the timeline
      if (e.shiftKey) {
        setIsPanning(true);
        setPanStart({
          x,
          viewStart: viewBounds.start.getTime(),
          viewEnd: viewBounds.end.getTime(),
        });
        return;
      }

      const date = xToDate(x, viewBounds, rect.width);

      if (mode === 'point') {
        setIsLiveMode(false);
        const pointDate = clampSelectionToNow
          ? new Date(Math.min(date.getTime(), Date.now()))
          : date;
        setSelectedPoint(pointDate);
        onDatePointChange?.(pointDate);
      } else {
        setIsLiveMode(false);
        const edge = getEdgeAtPosition(x, viewBounds, rect.width);

        if (edge) {
          setIsDraggingEdge(edge);
        } else {
          const clickedInRange = isPositionInSelectedRange(x, viewBounds, rect.width);

          if (clickedInRange && selectedRange) {
            const rangeStartX = dateToX(selectedRange.start, viewBounds, rect.width);
            setIsDraggingRange(true);
            setDragRangeOffset(x - rangeStartX);
          } else {
            setIsDragging(true);
            setDragStart(x);
            const initial = clampTimelineSelection(date, date, eventBounds, clampSelectionToNow);
            setSelectedRange(initial);
          }
        }
      }
    },
    [
      canvasRef,
      getViewBounds,
      getEventBounds,
      mode,
      onDatePointChange,
      getEdgeAtPosition,
      isPositionInSelectedRange,
      selectedRange,
      setIsLiveMode,
      setSelectedPoint,
      setSelectedRange,
      clampSelectionToNow,
    ]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      const viewBounds = getViewBounds();
      const eventBounds = getEventBounds();
      if (!canvas || !viewBounds || !eventBounds) return;

      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      setHoverTime(xToDate(x, viewBounds, rect.width));

      // Pan: shift view by delta
      if (isPanning && panStart) {
        const viewDuration = panStart.viewEnd - panStart.viewStart;
        const deltaX = x - panStart.x;
        const deltaTime = (deltaX / rect.width) * viewDuration;
        let newStart = panStart.viewStart - deltaTime;
        let newEnd = panStart.viewEnd - deltaTime;
        const eventStart = eventBounds.start.getTime();
        const eventEnd = eventBounds.end.getTime();
        if (newStart < eventStart) {
          newStart = eventStart;
          newEnd = newStart + viewDuration;
        }
        if (newEnd > eventEnd) {
          newEnd = eventEnd;
          newStart = newEnd - viewDuration;
        }
        setViewBounds({ start: new Date(newStart), end: new Date(newEnd) });
        return;
      }

      if (mode === 'range' && !isDragging && !isDraggingRange && !isDraggingEdge) {
        const edge = getEdgeAtPosition(x, viewBounds, rect.width);
        if (edge) {
          setIsHoveringEdge(edge);
          setIsHoveringRange(false);
        } else {
          setIsHoveringEdge(null);
          setIsHoveringRange(isPositionInSelectedRange(x, viewBounds, rect.width));
        }
      }

      const session = getSessionAtPosition(x, y, viewBounds, rect.width);

      if (session) {
        setHoveredSession(session);
        setHoveredDataBar(null);
        setTooltipPosition({ x: e.clientX, y: e.clientY - 80 });
      } else {
        setHoveredSession(null);
        const dataBar = getDataBarAtPosition(x, y, viewBounds, rect.width, rect.height);
        if (dataBar) {
          setHoveredDataBar(dataBar);
          setTooltipPosition({ x: e.clientX, y: e.clientY - 60 });
        } else {
          setHoveredDataBar(null);
          setTooltipPosition(null);
        }
      }

      if (mode === 'point') return;

      if (isDraggingEdge && selectedRange) {
        const newDate = xToDate(x, viewBounds, rect.width);

        if (isDraggingEdge === 'start') {
          if (newDate.getTime() >= selectedRange.end.getTime()) return;
          setSelectedRange(
            clampTimelineSelection(newDate, selectedRange.end, eventBounds, clampSelectionToNow)
          );
        } else {
          if (newDate.getTime() <= selectedRange.start.getTime()) return;
          setSelectedRange(
            clampTimelineSelection(selectedRange.start, newDate, eventBounds, clampSelectionToNow)
          );
        }
        return;
      }

      if (isDraggingRange && selectedRange) {
        const rangeDuration = selectedRange.end.getTime() - selectedRange.start.getTime();
        const newStartX = x - dragRangeOffset;
        const newStartDate = xToDate(newStartX, viewBounds, rect.width);
        const newEndDate = new Date(newStartDate.getTime() + rangeDuration);
        setSelectedRange(
          clampTimelineSelection(newStartDate, newEndDate, eventBounds, clampSelectionToNow)
        );
        return;
      }

      if (!isDragging || dragStart === null) return;

      const startDate = xToDate(Math.min(dragStart, x), viewBounds, rect.width);
      const endDate = xToDate(Math.max(dragStart, x), viewBounds, rect.width);
      setSelectedRange(clampTimelineSelection(startDate, endDate, eventBounds, clampSelectionToNow));
    },
    [
      canvasRef,
      getViewBounds,
      getEventBounds,
      mode,
      isPanning,
      panStart,
      isDragging,
      isDraggingRange,
      isDraggingEdge,
      dragStart,
      dragRangeOffset,
      selectedRange,
      getEdgeAtPosition,
      isPositionInSelectedRange,
      getSessionAtPosition,
      getDataBarAtPosition,
      setViewBounds,
      setSelectedRange,
      clampSelectionToNow,
    ]
  );

  const handleMouseUp = useCallback(() => {
    if (isPanning) {
      setIsPanning(false);
      setPanStart(null);
      return;
    }

    if (mode === 'point') return;

    if (isDraggingEdge && selectedRange && onDateRangeChange) {
      setIsDraggingEdge(null);
      onDateRangeChange(selectedRange.start, selectedRange.end);
    }

    if (isDraggingRange && selectedRange && onDateRangeChange) {
      setIsDraggingRange(false);
      setDragRangeOffset(0);
      onDateRangeChange(selectedRange.start, selectedRange.end);
    }

    if (isDragging && selectedRange && onDateRangeChange) {
      setIsDragging(false);
      setDragStart(null);
      onDateRangeChange(selectedRange.start, selectedRange.end);
    }
  }, [mode, isPanning, isDraggingEdge, isDraggingRange, isDragging, selectedRange, onDateRangeChange]);

  const handleMouseLeave = useCallback(() => {
    setHoveredSession(null);
    setHoveredDataBar(null);
    setTooltipPosition(null);
    setIsHoveringRange(false);
    setIsHoveringEdge(null);
    setHoverTime(null);
    if (isPanning || isDragging || isDraggingRange || isDraggingEdge) {
      handleMouseUp();
    }
  }, [isPanning, isDragging, isDraggingRange, isDraggingEdge, handleMouseUp]);

  const adjustStartTime = useCallback(
    (minutes: number) => {
      const eventBounds = getEventBounds();
      if (mode === 'range' && selectedRange && eventBounds) {
        setIsLiveMode(false);
        const newStart = new Date(selectedRange.start.getTime() + minutes * 60000);
        const newRange = clampTimelineSelection(
          newStart,
          selectedRange.end,
          eventBounds,
          clampSelectionToNow
        );
        setSelectedRange(newRange);
        if (adjustmentTimeoutRef.current) clearTimeout(adjustmentTimeoutRef.current);
        adjustmentTimeoutRef.current = window.setTimeout(() => {
          onDateRangeChange?.(newRange.start, newRange.end);
        }, 500);
      } else if (mode === 'point' && selectedPoint) {
        setIsLiveMode(false);
        let newPoint = new Date(selectedPoint.getTime() + minutes * 60000);
        if (clampSelectionToNow) {
          newPoint = new Date(Math.min(newPoint.getTime(), Date.now()));
        }
        setSelectedPoint(newPoint);
        if (adjustmentTimeoutRef.current) clearTimeout(adjustmentTimeoutRef.current);
        adjustmentTimeoutRef.current = window.setTimeout(() => {
          onDatePointChange?.(newPoint);
        }, 500);
      }
    },
    [
      mode,
      selectedRange,
      selectedPoint,
      onDateRangeChange,
      onDatePointChange,
      setIsLiveMode,
      setSelectedPoint,
      setSelectedRange,
      getEventBounds,
      clampSelectionToNow,
    ]
  );

  const adjustEndTime = useCallback(
    (minutes: number) => {
      const eventBounds = getEventBounds();
      if (mode === 'range' && selectedRange && eventBounds) {
        setIsLiveMode(false);
        const newEnd = new Date(selectedRange.end.getTime() + minutes * 60000);
        const newRange = clampTimelineSelection(
          selectedRange.start,
          newEnd,
          eventBounds,
          clampSelectionToNow
        );
        setSelectedRange(newRange);
        if (adjustmentTimeoutRef.current) clearTimeout(adjustmentTimeoutRef.current);
        adjustmentTimeoutRef.current = window.setTimeout(() => {
          onDateRangeChange?.(newRange.start, newRange.end);
        }, 500);
      } else if (mode === 'point' && selectedPoint) {
        setIsLiveMode(false);
        let newPoint = new Date(selectedPoint.getTime() + minutes * 60000);
        if (clampSelectionToNow) {
          newPoint = new Date(Math.min(newPoint.getTime(), Date.now()));
        }
        setSelectedPoint(newPoint);
        if (adjustmentTimeoutRef.current) clearTimeout(adjustmentTimeoutRef.current);
        adjustmentTimeoutRef.current = window.setTimeout(() => {
          onDatePointChange?.(newPoint);
        }, 500);
      }
    },
    [
      mode,
      selectedRange,
      selectedPoint,
      onDateRangeChange,
      onDatePointChange,
      setIsLiveMode,
      setSelectedPoint,
      setSelectedRange,
      getEventBounds,
      clampSelectionToNow,
    ]
  );

  useEffect(() => {
    return () => {
      if (adjustmentTimeoutRef.current) {
        clearTimeout(adjustmentTimeoutRef.current);
      }
    };
  }, []);

  return {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleMouseLeave,
    adjustStartTime,
    adjustEndTime,
    isDragging,
    isDraggingRange,
    isPanning,
    isDraggingEdge,
    isHoveringRange,
    isHoveringEdge,
    hoveredSession,
    hoveredDataBar,
    tooltipPosition,
    hoverTime,
  };
}
